// Поля просмотра элемента описаны данными, а не разметкой: новое поле сущности
// добавляется одной строкой здесь и появляется во всех местах просмотра сразу
// (промпт-бар, пикер, библиотека), а не в трёх копиях, которые разойдутся.
//
// Деталь показывает готовый «вид» (`ElementDetailsView`), а не сущность: то же
// окно открывается и на элементе библиотеки, и на его снимке в сообщении, где
// известны только имя, описание и картинки на момент генерации.

import type { TFunction } from "i18next";

import { fmtRelative } from "~/shared/lib/fmt";

import type { Element } from "../types";

export interface ElementDetailField {
    key: string;
    label: string;
    value: string;
    /** Длинный текст (описание) — абзацем под меткой, а не строкой справа. */
    multiline?: boolean;
}

export interface ElementDetailsView {
    /** Заголовок детали — имя элемента. */
    name: string;
    /** Галерея: все известные картинки по порядку. */
    images: readonly string[];
    fields: ElementDetailField[];
}

/** Прочерк вместо пустого значения: поле видно всегда, иначе список «прыгает» между элементами. */
const EMPTY_VALUE = "—";

export function getElementDetailFields(t: TFunction, element: Element): ElementDetailField[] {
    return [
        {
            key: "description",
            label: t("elements:details.fields.description"),
            value: element.description || EMPTY_VALUE,
            multiline: true,
        },
        {
            key: "type",
            label: t("elements:details.fields.type"),
            value: t(`elements:type.${element.elementType}`),
        },
        {
            key: "source",
            label: t("elements:details.fields.source"),
            value: t(`elements:source.${element.source}`),
        },
        {
            key: "images",
            label: t("elements:details.fields.images"),
            value: t("elements:card.imagesCount", { count: element.images.length }),
        },
        {
            key: "created",
            label: t("elements:details.fields.created"),
            value: fmtRelative(element.createdAt),
        },
        {
            key: "updated",
            label: t("elements:details.fields.updated"),
            value: element.updatedAt ? fmtRelative(element.updatedAt) : EMPTY_VALUE,
        },
    ];
}

/** Полный вид элемента библиотеки: все поля сущности. */
export function toElementDetailsView(t: TFunction, element: Element): ElementDetailsView {
    return {
        name: element.name,
        images: element.images.map((image) => image.s3Url),
        fields: getElementDetailFields(t, element),
    };
}

/** Снимок элемента, сохранённый вместе с запросом генерации. */
export interface ElementSnapshot {
    name: string;
    description: string;
    imageUrls: readonly string[];
}

/**
 * Вид по снимку запроса — для истории сообщений. Полей меньше, чем у сущности, и
 * это не упущение: снимок хранит только имя, описание и картинки. Тип, источник
 * и даты пришлось бы брать из текущей библиотеки, а тогда сообщение перестанет
 * показывать то, с чем генерация реально ушла.
 */
export function toSnapshotDetailsView(t: TFunction, snapshot: ElementSnapshot): ElementDetailsView {
    return {
        name: snapshot.name,
        images: snapshot.imageUrls,
        fields: [
            {
                key: "description",
                label: t("elements:details.fields.description"),
                value: snapshot.description || EMPTY_VALUE,
                multiline: true,
            },
            {
                key: "images",
                label: t("elements:details.fields.images"),
                value: t("elements:card.imagesCount", { count: snapshot.imageUrls.length }),
            },
        ],
    };
}
